import React, { ReactNode, useMemo, useState } from "react";

import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";

import { getFieldPracticesAdmin, getFullFarmDetails } from "services";

import { formatNumber, useGetFieldVerifications, useTranslation } from "utils";

import {
  CROP_TYPE_FCPO,
  FIELD_VERIFICATION_FILTER,
  FieldVerification,
  FieldVerificationFilter,
  FieldVerificationSort,
  PRACTICE_SLUG,
  PracticeSlug,
} from "models";

import { Col, Icon, IconButton, LinedCard, Pagination, Row, SkeletonWrapper, TableFieldPanel, Tag } from "components";

import { FieldDrawer } from "pages/admin/verifications/components/drawers/FieldDrawer";
import { SearchAndFilter } from "pages/admin/verifications/components/misc/SearchAndFilter";
import { VerificationStatusDisplay } from "pages/admin/verifications/components/misc/VerificationStatusDisplay";
import { FieldCropData, filterFields } from "pages/admin/verifications/utils/cropFilterUtils";
import { getFieldVerificationStatus, paginateItems, sortFields } from "pages/admin/verifications/utils/fieldVerificationUtils";

interface PracticeListProps {
  farmId: string;
  practiceSlugs: PracticeSlug[];
  renderExtraColumns?: (field: FieldVerification) => ReactNode;
  renderTimeline: (fieldId: string) => ReactNode;
  renderExpandedContent: (field: FieldVerification) => ReactNode;
  additionalDrawerProps?: object;
}

export const PracticeList = ({
  farmId,
  practiceSlugs,
  renderExtraColumns,
  renderTimeline,
  renderExpandedContent,
  additionalDrawerProps = {},
}: PracticeListProps) => {
  const [page, setPage] = useState(1);
  const defaultItemsPerPage = 20;
  const [itemsPerPage, setItemsPerPage] = useState(defaultItemsPerPage);
  const [expandedRows, setExpandedRows] = useState<string[]>([]);
  const [showDrawer, setShowDrawer] = useState(false);
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);
  const [filter, setFilter] = useState<FieldVerificationFilter>();
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<FieldVerificationSort>();

  const queryClient = useQueryClient();
  const { t } = useTranslation(["practices"], { useSuspense: false });

  // Fetch all fields once (filtering/sorting/pagination done locally)
  const { data: fieldData, isLoading } = useGetFieldVerifications({
    farmId,
    search: search || undefined,
    filter: filter === FIELD_VERIFICATION_FILTER.allFields ? filter : undefined,
  });

  const { data: farmDetails } = useQuery({
    queryKey: ["admin-farm-details", farmId],
    queryFn: async () => {
      const response = await getFullFarmDetails({ id: farmId });
      return response.data;
    },
    enabled: !!farmId,
  });

  // Fetch crops for all fields if this is the crops practice
  const isCropsPractice = practiceSlugs.includes(PRACTICE_SLUG.CROPS);
  const fieldIds = useMemo(() => fieldData?.fields?.map((f) => f.id) || [], [fieldData?.fields]);

  const cropsQueries = useQueries({
    queries: fieldIds.map((fieldId) => ({
      queryKey: ["admin-field-practices", fieldId],
      queryFn: () => getFieldPracticesAdmin({ fieldId }),
      enabled: isCropsPractice && fieldIds.length > 0,
      staleTime: 1000 * 60 * 60 * 24, // 1 day
    })),
  });

  // Track crops data for dependency tracking
  const cropsData = useMemo(
    () =>
      cropsQueries.map((query, index) => ({
        fieldId: fieldIds[index],
        data: query.data,
        updatedAt: query.dataUpdatedAt,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cropsQueries.map((q) => q.dataUpdatedAt).join(","), fieldIds.join(",")]
  );

  // Create a map of fieldId -> crops for efficient filtering
  const fieldCropsMap = useMemo(() => {
    if (!isCropsPractice) return null;

    const map = new Map<string, FieldCropData[]>();

    cropsData.forEach(({ fieldId, data }) => {
      if (data?.crops && data.crops.length > 0 && fieldId) {
        map.set(
          fieldId,
          data.crops.map((crop) => {
            // Translate crop name if translationSlug exists, otherwise use cropName
            // Fallback to "Unknown Crop" if neither is available
            const cropName = crop.cropName || (crop.translationSlug ? t(`crops.${crop.translationSlug}`) : "Unknown Crop");

            return {
              cropName,
              year: crop.year,
              cropType: crop.cropType,
            };
          })
        );
      }
    });

    return map;
  }, [isCropsPractice, cropsData, t]);

  // Extract unique crops from all fields
  const uniqueCrops = useMemo(() => {
    if (!fieldCropsMap || fieldCropsMap.size === 0) return [];

    const cropSet = new Set<string>();
    fieldCropsMap.forEach((crops) => {
      crops.forEach((crop) => {
        if (crop.cropName && crop.cropName.trim() !== "") {
          cropSet.add(crop.cropName);
        }
      });
    });

    return Array.from(cropSet).sort();
  }, [fieldCropsMap]);

  // Extract available crop types from all fields
  const availableCropTypes = useMemo(() => {
    if (!fieldCropsMap || fieldCropsMap.size === 0) return [];

    const cropTypeSet = new Set<CROP_TYPE_FCPO>();
    fieldCropsMap.forEach((crops) => {
      crops.forEach((crop) => {
        if (crop.cropType) {
          cropTypeSet.add(crop.cropType);
        }
      });
    });

    return Array.from(cropTypeSet);
  }, [fieldCropsMap]);

  // Extract available years from all fields
  const availableYears = useMemo(() => {
    if (!fieldCropsMap || fieldCropsMap.size === 0) return [];

    const yearSet = new Set<number>();
    fieldCropsMap.forEach((crops) => {
      crops.forEach((crop) => {
        if (crop.year) {
          yearSet.add(crop.year);
        }
      });
    });

    return Array.from(yearSet).sort((a, b) => a - b);
  }, [fieldCropsMap]);

  // Apply local filtering and sorting
  const processedFields = useMemo(() => {
    if (!fieldData?.fields) return [];
    const filtered = filterFields(fieldData.fields, filter, fieldCropsMap);
    const sorted = sortFields(filtered, sort);
    return sorted;
  }, [fieldData?.fields, filter, sort, fieldCropsMap]);

  // Apply local pagination
  const paginatedFields = useMemo(() => paginateItems(processedFields, page, itemsPerPage), [processedFields, page, itemsPerPage]);

  const handlePageChange = (newPage: number, pageSize: number) => {
    setPage(newPage);
    setItemsPerPage(pageSize);
  };

  const handleFilterChange = (newFilter: string) => {
    setFilter(newFilter as FieldVerificationFilter);
    setPage(1); // Reset to first page when filter changes
  };

  const handleSortChange = (newSort: FieldVerificationSort | undefined) => {
    setSort(newSort);
    setPage(1); // Reset to first page when sort changes
  };

  const toggleRowExpansion = (fieldId: string) => {
    setExpandedRows((prev) => (prev.includes(fieldId) ? prev.filter((id) => id !== fieldId) : [...prev, fieldId]));
  };

  const handleDrawerOpen = (fieldId: string) => {
    setSelectedFieldId(fieldId);
    setShowDrawer(true);
  };

  const handleDrawerClose = () => {
    setShowDrawer(false);
    setTimeout(() => setSelectedFieldId(null), 200);
  };

  const handleDataChange = () => {
    queryClient.invalidateQueries({ queryKey: ["admin-field-verifications", farmId] });
  };

  return (
    <>
      <SearchAndFilter
        setFilter={handleFilterChange}
        setPage={setPage}
        setSearch={setSearch}
        setSort={handleSortChange}
        type="practice"
        practiceSlug={practiceSlugs}
        uniqueCrops={uniqueCrops}
        availableCropTypes={availableCropTypes}
        availableYears={availableYears}
      />

      <Row>
        <Col>
          {!isLoading && processedFields.length === 0 && <p>There are no fields to verify.</p>}

          <div className="verification-crop-table">
            {/* Table Header */}
            <Row align="middle" className="table-header">
              <Col md={7}>
                <span className="paragraph-xs-bold">Field Name</span>
              </Col>
              <Col md={3}>
                <span className="paragraph-xs-bold">Size</span>
              </Col>
              <Col md={3}>
                <span className="paragraph-xs-bold">Redzone</span>
              </Col>
              {renderExtraColumns && (
                <Col md={2}>
                  <span className="paragraph-xs-bold">Editing Request</span>
                </Col>
              )}
              <Col md={3}>
                <span className="paragraph-xs-bold">Start Year</span>
              </Col>
              <Col md={4}>
                <span className="paragraph-xs-bold">Verification Status</span>
              </Col>
              <Col md={2}></Col>
            </Row>

            {paginatedFields.map((field) => {
              const isExpanded = expandedRows.includes(field.id);
              return (
                <LinedCard
                  className="margin-bottom-100"
                  key={field.id}
                  primary={
                    <Row align="middle">
                      <Col md={7}>
                        {/* @ts-expect-error not in ts yet */}
                        <TableFieldPanel
                          field={field}
                          badges={
                            field.commentCount > 0 && (
                              <>
                                <Icon iconName={"tabler/message-2"} className="icon inline" size="s" />
                                {field.commentCount}
                              </>
                            )
                          }
                          isAdmin
                        />
                      </Col>
                      <Col md={3}>
                        <span className="paragraph-xs">{formatNumber({ number: field.size, decimalPlaces: 4 })}ha</span>
                      </Col>
                      <Col md={3}>
                        {field.redzone && (
                          <Tag colour={field.redzone.confirmed ? "blue" : "red"} text={field.redzone.confirmed ? "confirmed redzone" : "redzone warning"} />
                        )}
                      </Col>
                      {renderExtraColumns && <Col md={2}>{renderExtraColumns(field)}</Col>}
                      <Col md={3}>
                        <span className="paragraph-xs">
                          {field.practiceParticipations
                            .filter(({ practiceSlug: slug }) => practiceSlugs.includes(slug))
                            .sort((a, b) => a.startYear - b.startYear)[0]?.startYear ?? "unknown"}
                        </span>
                      </Col>
                      <Col md={4}>
                        <SkeletonWrapper isLoading={isLoading} type="button" buttonShape="round" buttonSize="small" className="margin-0">
                          <Status field={field} />
                        </SkeletonWrapper>
                      </Col>
                      <Col md={2}>
                        <div className="flex-center-align gap-25">
                          <IconButton
                            iconName="klim/field"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDrawerOpen(field.id);
                            }}
                          />
                          <IconButton iconName={isExpanded ? "tabler/chevron-up" : "tabler/chevron-down"} onClick={() => toggleRowExpansion(field.id)} />
                        </div>
                      </Col>
                    </Row>
                  }
                  secondary={renderTimeline(field.id)}
                  tertiary={isExpanded && renderExpandedContent(field)}
                />
              );
            })}
          </div>

          {/* Pagination */}
          {processedFields.length > 0 && (
            <Pagination
              onChange={handlePageChange}
              total={processedFields.length}
              current={page}
              pageSizeOptions={[10, 20, 50, 100]}
              defaultPageSize={defaultItemsPerPage}
              pageSize={itemsPerPage}
              hideOnSinglePage={true}
              showSizeChanger={true}
            />
          )}
        </Col>
      </Row>

      {/* Field Drawer */}
      {selectedFieldId && fieldData?.fields && (
        <FieldDrawer
          field={fieldData.fields.find((field) => field.id === selectedFieldId)!}
          farm={{
            addressStreet: farmDetails?.addressStreet ?? "",
            addressNumber: farmDetails?.addressNumber ?? "",
            addressCity: farmDetails?.addressCity ?? "",
            zip: farmDetails?.zip ?? "",
            region: farmDetails?.region ?? "",
            country: farmDetails?.country ?? "",
          }}
          farmId={farmId}
          visible={showDrawer}
          loadingFields={isLoading}
          onClose={handleDrawerClose}
          onDataChange={handleDataChange}
          payoutApproved={false}
          {...additionalDrawerProps}
        />
      )}
    </>
  );
};

const Status = ({ field }: { field: FieldVerification }) => (
  <div className="flex-center-align">
    <VerificationStatusDisplay status={getFieldVerificationStatus(field?.verifications)} />
  </div>
);
